/**
 * SSE (Server-Sent Events) Context
 *
 * Provides a real-time event stream from the backend.
 * Replaces SocketContext — uses native EventSource (no client library needed).
 *
 * Cross-tab sharing: Only the leader tab (via tabLeader.js) opens an SSE
 * connection. Events are broadcast to follower tabs via BroadcastChannel.
 * This reduces connections from N (one per tab) to 1.
 *
 * Company scoping is automatic: the JWT token carries the user's company_id,
 * so the backend only sends events for the authenticated user's company.
 */

import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from "react";
import Cookies from "universal-cookie";
import { onLeaderChange } from "../utils/tabLeader";
import { createAuthorizedEventSource } from "../utils/sseClient";
import { hasValidToken } from "../utils/authSession";
import logger from "../utils/logger";
import { emitFrontendTelemetry } from "../utils/frontendTelemetry";



const SSEContext = createContext(null);

const cookies = new Cookies();

const SSE_CHANNEL_NAME = 'sse-events';
const SSE_EVENT_NAMES = ['task:updated', 'task:detail:updated', 'bulk:progress'];

/**
 * Get the Node API base URL (same logic as ApiServiceNode.js)
 */
const getApiUrl = () =>
    process.env.VITE_API_URL || process.env.VITE_NODE_API_URL || "http://localhost:3001";

export function SSEProvider({ children }) {
    const listenersRef = useRef(new Map()); // eventName -> Set<callback>
    const eventSourceRef = useRef(null);
    const reconnectTimerRef = useRef(null);
    const reconnectAttemptRef = useRef(0);
    const [connected, setConnected] = useState(false);
    const broadcastRef = useRef(null);
    const isLeaderRef = useRef(false);

    const MAX_RECONNECT_DELAY = 30000;
    const pausedRef = useRef(false); // true when tab is hidden

    /**
     * Close the SSE connection and cancel any pending reconnect
     */
    const disconnect = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = null;
        }
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setConnected(false);
        reconnectAttemptRef.current = 0;
    }, []);

    /**
     * Dispatch an incoming SSE event to all registered listeners
     */
    const dispatch = useCallback((eventName, data) => {
        const callbacks = listenersRef.current.get(eventName);
        if (!callbacks) return;
        for (const cb of callbacks) {
            try {
                cb(data);
            } catch (err) {
                logger.error("SSE listener error:", err);
            }
        }
    }, []);

    /**
     * Broadcast an SSE event to other tabs via BroadcastChannel
     */
    const broadcastEvent = useCallback((eventName, data) => {
        if (broadcastRef.current) {
            try {
                broadcastRef.current.postMessage({ eventName, data });
            } catch (_e) { /* ignore */ }
        }
    }, []);

    /**
     * Connect (or reconnect) the EventSource — only called by the leader tab
     */
    const connect = useCallback(() => {
        // Clean up any previous connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }

        const token = cookies.get("tokenR");
        if (!hasValidToken(token)) {
            // No valid token yet (logged out or expired) — retry later.
            reconnectTimerRef.current = setTimeout(() => connect(), 5000);
            return;
        }

        const url = `${getApiUrl()}/sse/events`;
        const es = createAuthorizedEventSource(url);
        eventSourceRef.current = es;

        es.onopen = () => {
            logger.info("SSE connected (leader tab)");
            setConnected(true);
            reconnectAttemptRef.current = 0;
        };

        // Listen for named events and broadcast to other tabs
        for (const eventName of SSE_EVENT_NAMES) {
            es.addEventListener(eventName, (e) => {
                try {
                    const data = JSON.parse(e.data);
                    dispatch(eventName, data);
                    broadcastEvent(eventName, data);
                } catch (_e) { /* ignore malformed data */ }
            });
        }

        es.onerror = () => {
            // EventSource fires error on any connection issue (including 401)
            es.close();
            eventSourceRef.current = null;
            setConnected(false);

            // Don't reconnect if tab is hidden or no longer leader
            if (pausedRef.current || !isLeaderRef.current) return;

            // Stop reconnect loops when auth is no longer valid.
            const nextToken = cookies.get("tokenR");
            if (!hasValidToken(nextToken)) return;

            // Exponential backoff: 1s, 2s, 4s, 8s, ... max 30s
            const attempt = reconnectAttemptRef.current++;
            const delay = Math.min(1000 * Math.pow(2, attempt), MAX_RECONNECT_DELAY);
            logger.debug(`SSE reconnecting in ${delay}ms (attempt ${attempt + 1})`);
            void emitFrontendTelemetry("frontend.sse.disconnected", {
                area: "sse",
                action: "reconnect_scheduled",
                url,
                retryCount: attempt + 1,
                durationMs: delay,
                message: "EventSource disconnected; reconnect scheduled",
            });
            reconnectTimerRef.current = setTimeout(() => connect(), delay);
        };
    }, [dispatch, broadcastEvent]);

    useEffect(() => {
        // Set up BroadcastChannel for cross-tab SSE event sharing
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                broadcastRef.current = new BroadcastChannel(SSE_CHANNEL_NAME);
                // Follower tabs receive events from the leader
                broadcastRef.current.onmessage = (event) => {
                    if (event.data && event.data.eventName) {
                        dispatch(event.data.eventName, event.data.data);
                        // Follower tabs are "connected" when they receive events
                        if (!isLeaderRef.current) {
                            setConnected(true);
                        }
                    }
                };
            }
        } catch (_e) { /* BroadcastChannel not supported — each tab connects independently */ }

        // Leader election: only the leader tab opens the SSE connection
        const unsubLeader = onLeaderChange((amLeader) => {
            isLeaderRef.current = amLeader;
            if (amLeader) {
                connect();
            } else {
                disconnect();
            }
        });

        // Pause SSE when tab is hidden, resume when visible
        const handleVisibilityChange = () => {
            if (document.hidden) {
                pausedRef.current = true;
                disconnect();
            } else {
                pausedRef.current = false;
                if (isLeaderRef.current) {
                    connect();
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
            disconnect();
            unsubLeader();
            if (broadcastRef.current) {
                try { broadcastRef.current.close(); } catch (_e) { /* ignore */ }
            }
        };
    }, [connect, disconnect, dispatch]);

    /**
     * Subscribe to an SSE event by name. Returns an unsubscribe function.
     *
     * Usage:
     *   const unsubscribe = subscribe("task:updated", (data) => { ... });
     *   return () => unsubscribe();
     */
    const subscribe = useCallback((eventName, callback) => {
        if (!listenersRef.current.has(eventName)) {
            listenersRef.current.set(eventName, new Set());
        }
        listenersRef.current.get(eventName).add(callback);

        return () => {
            const set = listenersRef.current.get(eventName);
            if (set) {
                set.delete(callback);
                if (set.size === 0) {
                    listenersRef.current.delete(eventName);
                }
            }
        };
    }, []);

    const value = { subscribe, connected };

    return (
        <SSEContext.Provider value={value}>
            {children}
        </SSEContext.Provider>
    );
}

/**
 * Hook to access SSE context
 */
export function useSSE() {
    const context = useContext(SSEContext);
    if (!context) {
        // Return a no-op if not inside SSEProvider (backwards compatibility)
        return { subscribe: () => () => {}, connected: false };
    }
    return context;
}

export default SSEContext;
